// Settings → Calendar tab (event colours).
//
// Source-of-truth: /var/www/kitchenos/portal-standalone.html
//   TYPES table:             line 25258  (10 event types + default colours)
//   renderSettingsPanel:     line 25324
//   injectLegend:            line 25304
//   localStorage key:        'kos_cal_colours_v1' (line 25270)
//
// Event-type ids and their default colours match the Calendar page exactly
// (apps/retailer/src/pages/calendar/page.ts TYPES) so colour changes here
// flow through to the rendered calendar via localStorage.
//
// Master-doc colours differ slightly from the standalone for a few rows
// (e.g. team is lilac #A78BFA in the spec but #6366f1 in the standalone).
// We follow the master doc — it's the customer-facing spec — but keep
// aliases so legacy saved values keep working.
//
// Backend
// -------
// No retailer-facing `/api/portal/settings/calendar-colours` endpoint exists
// today (verified against /var/www/kitchenos-api/server.js as of 2026-05).
// Persistence is therefore localStorage-first (immediate UI feedback) with
// an optimistic POST that falls back to a "saved locally" toast when the
// server endpoint 404s.
//
//   TODO: PUT  /api/portal/settings/calendar-colours  { colours: {id: hex} }
//   TODO: GET  /api/portal/settings/calendar-colours  → { colours }

import { ApiError } from "@tkos/api-client";
import { iconMarkup } from "@tkos/shared-ui";
import type { SettingsTab } from "../types";
import type { PageContext } from "../../../shell/types";

import "./calendar.css";

// ── Types & defaults ─────────────────────────────────────────────────────────

interface EventTypeMeta {
  /** Stable id — matches the Calendar page's `EventType`. */
  id: string;
  /** Human label shown in the row. */
  label: string;
  /** Short sub-label. */
  desc: string;
  /** Default hex colour (per master doc). */
  def: string;
}

/**
 * Order matches the master-spec brief exactly (10 types). Default hex codes
 * are from the master doc; they may differ from the standalone portal seed
 * by a shade but the standalone reads from the same localStorage key so
 * any saved override beats the default either way.
 */
const TYPES: ReadonlyArray<EventTypeMeta> = [
  { id: "enquiry", label: "New Enquiries", desc: "Leads and first contacts", def: "#F59E0B" },
  { id: "delivery", label: "Deliveries", desc: "Inbound / outbound shipments", def: "#3B82F6" },
  { id: "booking", label: "Bookings / Consultations", desc: "Design appointments", def: "#10B981" },
  { id: "installation", label: "Installations", desc: "Fitter / install schedule", def: "#8B5CF6" },
  { id: "personal", label: "Personal", desc: "Private / off-diary items", def: "#6B7280" },
  { id: "followup", label: "Follow-ups", desc: "Chase-ups & reminders", def: "#FACC15" },
  { id: "survey", label: "Site Surveys", desc: "Measure-ups / site visits", def: "#14B8A6" },
  { id: "snag", label: "Snags", desc: "Post-install issues", def: "#F97316" },
  { id: "payment", label: "Payments", desc: "Invoices, payments due", def: "#EF4444" },
  { id: "team", label: "Team", desc: "Staff events", def: "#A78BFA" },
];

const LS_KEY = "tkos.calendar.colours";
/** Legacy key written by the standalone portal — we read it as a fallback. */
const LS_KEY_LEGACY = "kos_cal_colours_v1";

type ColourMap = Record<string, string>;

// ── Helpers ──────────────────────────────────────────────────────────────────

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function isMissingEndpoint(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.status === 501);
}

function defaults(): ColourMap {
  const out: ColourMap = {};
  for (const t of TYPES) out[t.id] = t.def;
  return out;
}

function isHex(value: string): boolean {
  return /^#[0-9a-f]{6}$/i.test(value);
}

function loadFromStorage(): ColourMap {
  const out = defaults();
  // New key first, then the legacy standalone key.
  for (const key of [LS_KEY, LS_KEY_LEGACY]) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        for (const t of TYPES) {
          const v = (parsed as Record<string, unknown>)[t.id];
          if (typeof v === "string" && isHex(v)) out[t.id] = v;
        }
      }
    } catch {
      // ignore — corrupted storage shouldn't break the tab.
    }
  }
  return out;
}

function saveToStorage(colours: ColourMap): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(colours));
    // Keep the legacy key in sync so the standalone calendar legend still
    // picks up the same overrides while both implementations coexist.
    localStorage.setItem(LS_KEY_LEGACY, JSON.stringify(colours));
  } catch {
    // Quota / SecurityError — swallow; the UI still works in-memory.
  }
}

function esc(s: string): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// ── Tab definition ───────────────────────────────────────────────────────────

const calendarTab: SettingsTab = {
  id: "calendar",
  label: "Calendar",
  icon: iconMarkup("calendar", { size: "sm" }),

  mount(container: HTMLElement, ctx: PageContext) {
    const ac = new AbortController();
    let disposed = false;

    // Working state (mutated on every input change, persisted to localStorage
    // immediately for instant feedback in the rest of the OS).
    const working: ColourMap = loadFromStorage();

    // ── Scaffold ──────────────────────────────────────────────────────────
    const root = document.createElement("section");
    root.className = "set-cal-root";
    root.innerHTML = `
      <header class="set-cal-header">
        <h3 class="set-section-title">Calendar event colours</h3>
        <p class="set-help">
          Choose a colour for each type of calendar event. Changes preview
          instantly in the panel below — click <b>Save</b> to sync them to
          your account.
        </p>
      </header>

      <div class="set-cal-rows" data-role="rows"></div>

      <section class="set-cal-preview" aria-label="Live preview">
        <div class="set-cal-preview__label">Preview</div>
        <div class="set-cal-preview__chips" data-role="preview"></div>
      </section>

      <div class="set-cal-actions">
        <button type="button" class="set-btn set-btn-secondary" data-action="reset-all">
          Reset all to defaults
        </button>
        <button type="button" class="set-btn" data-action="save">
          Save
        </button>
      </div>
    `;
    container.appendChild(root);

    const rowsEl = root.querySelector<HTMLDivElement>("[data-role='rows']")!;
    const previewEl = root.querySelector<HTMLDivElement>("[data-role='preview']")!;

    // ── Renderers ─────────────────────────────────────────────────────────

    function renderRows(): void {
      rowsEl.innerHTML = TYPES.map((t) => {
        const c = working[t.id] ?? t.def;
        return `
          <div class="set-cal-row" data-type="${esc(t.id)}">
            <div class="set-cal-row__info">
              <div class="set-cal-row__label">${esc(t.label)}</div>
              <div class="set-cal-row__desc">${esc(t.desc)}</div>
            </div>
            <span
              class="set-cal-row__swatch"
              data-role="swatch"
              style="background:${esc(c)}"
              aria-hidden="true"
            ></span>
            <input
              type="color"
              class="set-cal-row__input"
              data-role="input"
              value="${esc(c)}"
              aria-label="${esc(t.label)} colour"
            />
            <button
              type="button"
              class="set-cal-row__reset"
              data-role="reset"
            >Reset</button>
          </div>
        `;
      }).join("");
    }

    function renderPreview(): void {
      previewEl.innerHTML = TYPES.map((t) => {
        const c = working[t.id] ?? t.def;
        return `
          <span
            class="set-cal-preview__chip"
            style="background:${esc(c)}"
            title="${esc(t.label)}"
          >${esc(t.label)}</span>
        `;
      }).join("");
    }

    function syncRow(typeId: string): void {
      const c = working[typeId] ?? defaults()[typeId];
      const row = rowsEl.querySelector<HTMLElement>(
        `.set-cal-row[data-type='${cssEscape(typeId)}']`,
      );
      if (!row) return;
      const swatch = row.querySelector<HTMLElement>("[data-role='swatch']");
      const input = row.querySelector<HTMLInputElement>("[data-role='input']");
      if (swatch) swatch.style.background = c;
      if (input && input.value.toLowerCase() !== c.toLowerCase()) input.value = c;

      // Update just this chip in the preview without rebuilding the whole strip.
      const chip = previewEl.querySelectorAll<HTMLElement>(".set-cal-preview__chip");
      const idx = TYPES.findIndex((t) => t.id === typeId);
      if (idx >= 0 && chip[idx]) chip[idx].style.background = c;
    }

    function cssEscape(s: string): string {
      const ce = (window as unknown as { CSS?: { escape?: (s: string) => string } })
        .CSS?.escape;
      return ce ? ce(s) : s.replace(/[^a-zA-Z0-9_-]/g, "_");
    }

    // ── Event handlers ────────────────────────────────────────────────────

    function onRowsInput(ev: Event): void {
      const input = ev.target as HTMLInputElement;
      if (input.dataset.role !== "input") return;
      const row = input.closest<HTMLElement>(".set-cal-row");
      if (!row) return;
      const typeId = row.dataset.type ?? "";
      if (!typeId) return;
      const val = input.value;
      if (!isHex(val)) return;
      working[typeId] = val;
      // Persist on every keystroke so live calendar preview is instant.
      saveToStorage(working);
      syncRow(typeId);
    }

    function onRowsClick(ev: Event): void {
      const target = ev.target as HTMLElement;
      const btn = target.closest<HTMLButtonElement>("[data-role='reset']");
      if (!btn) return;
      const row = btn.closest<HTMLElement>(".set-cal-row");
      const typeId = row?.dataset.type ?? "";
      const meta = TYPES.find((t) => t.id === typeId);
      if (!meta) return;
      working[typeId] = meta.def;
      saveToStorage(working);
      syncRow(typeId);
    }

    function onResetAll(): void {
      const def = defaults();
      for (const k of Object.keys(working)) delete working[k];
      Object.assign(working, def);
      saveToStorage(working);
      renderRows();
      renderPreview();
      ctx.showToast("Calendar colours reset to defaults", "info");
    }

    async function onSave(): Promise<void> {
      const saveBtn = root.querySelector<HTMLButtonElement>(
        "[data-action='save']",
      );
      if (saveBtn) saveBtn.disabled = true;
      // TODO: PUT /api/portal/settings/calendar-colours
      try {
        await ctx.api("/portal/settings/calendar-colours", {
          method: "PUT",
          signal: ac.signal,
          body: JSON.stringify({ colours: working }),
        });
        if (disposed) return;
        ctx.showToast("Calendar colours saved", "success");
      } catch (err) {
        if (isAbortError(err)) return;
        if (isMissingEndpoint(err)) {
          ctx.showToast(
            "Colours saved locally (server endpoint pending)",
            "info",
          );
          return;
        }
        ctx.showToast("Couldn't save colours", "error");
      } finally {
        if (!disposed && saveBtn) saveBtn.disabled = false;
      }
    }

    // ── Wire-up ───────────────────────────────────────────────────────────

    renderRows();
    renderPreview();

    rowsEl.addEventListener("input", onRowsInput);
    rowsEl.addEventListener("click", onRowsClick);

    const resetAllBtn = root.querySelector<HTMLButtonElement>(
      "[data-action='reset-all']",
    );
    resetAllBtn?.addEventListener("click", onResetAll);

    const saveBtn = root.querySelector<HTMLButtonElement>("[data-action='save']");
    saveBtn?.addEventListener("click", () => void onSave());

    // ── Cleanup ────────────────────────────────────────────────────────────
    return (): void => {
      disposed = true;
      rowsEl.removeEventListener("input", onRowsInput);
      rowsEl.removeEventListener("click", onRowsClick);
      resetAllBtn?.removeEventListener("click", onResetAll);
      ac.abort();
    };
  },
};

export default calendarTab;
